import React from 'react';
import { connect, history, useIntl } from 'umi';
import { Button, Card, Justify, Layout, notification, PagingQuery, SearchBox, Table } from 'tea-component';
import { DATALAB_EDITOR, SYSTEM_NAME } from '@dbl/core/global';
import AccessSettingModal from '@/pages/datalab/components/AccessSettingModal';
import ModelModal from './components/ModelModal';
import DetailModal from './components/DetailModal';
import { YAML_TEST_RESULT_NOTIFICATION_CLASS } from './constant';
import McpAddressHeader from './components/McpAddressHeader';
import { useModelManagementCenter } from './hooks/useModelManagementCenter';
import './index.scss';

const { Content } = Layout;
const { pageable, scrollable, sortable, autotip } = Table.addons;

const ModelManagementCenter = (props: any) => {
  const { allPrivileges, gameCode } = props || {};
  const { locale, formatMessage } = useIntl();

  const {
    // state
    searchWords,
    searchValue,
    setSearchValue,
    setSearchWords,
    sort,
    quickValue,
    pageInfo,
    records,
    loading,
    total,
    error,
    detailVisible,
    setDetailVisible,
    detailData,
    detailLoading,
    detailError,
    accessInfo,
    setAccessInfo,
    deleteInfo,
    deleteVisible,
    setDeleteVisible,
    deleteLoading,
    // table
    sortColumns,
    columns,
    // handlers
    getTableData,
    handleClearSearch,
    handleSearchWithKeyword,
    onPagingChange,
    onSortChange,
    deleteDetailYaml,
  } = useModelManagementCenter(props);

  return (
    <Content >
      <Content.Header
        title={<McpAddressHeader />}
      />
      <Content.Body
        full
        className="dbl-model-management"
      >
        <Justify
          className="dbl-model-management-buttom"
          left={
            <Button
              type="primary"
              disabled={!allPrivileges?.includes(DATALAB_EDITOR.AddModelBtn)}
              onClick={() => {
                history.push(`/${SYSTEM_NAME}/${gameCode}/DataManagement/ModelManagementCenter/Create`);
              }}
            >
              {formatMessage({ id: "dbl_model_management_create_model" })}
            </Button>
          }
          right={
            <SearchBox
              className="dbl-model-management-search"
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                if (!value) {
                  setSearchWords('');
                  handleClearSearch();
                }
              }}
              onSearch={(keyword) => {
                const kw = keyword || '';
                setSearchWords(kw);
                if (kw.length !== 0) {
                  handleSearchWithKeyword(kw);
                } else {
                  handleClearSearch();
                }
              }}
              onClear={() => {
                setSearchWords('');
              }}
              placeholder={formatMessage({ id: "dbl_model_management_search_placeholder" })}
            />
          }
        />
        <Card>
          <Card.Body>
            <Table
              recordKey="name"
              className={`dbl-model-management-table dbl-model-management-table-${locale?.toLowerCase()}`}
              columns={columns}
              records={records}
              addons={[
                scrollable({
                  // maxHeight: 350,
                  minWidth: 1036,
                }),
                pageable({
                  isPagingReset: true,
                  pageSize: pageInfo?.pageSize,
                  pageIndex: pageInfo?.pageIndex,
                  pageSizeOptions: [10, 20, 50, 100],
                  recordCount: total,
                  stateText: formatMessage({ id: "dbl_model_management_total" }, { total }),
                  stateTextVisible: total > 10,
                  pageSizeVisible: total > 10,
                  pageIndexVisible: total > 10,
                  jumpVisible: total > 10,
                  endJumpVisible: total > 10,
                  onPagingChange: (query: PagingQuery) => {
                    onPagingChange(query);
                  },
                }),
                sortable({
                  columns: sortColumns,
                  value: [sort],
                  onChange: (value) => {
                    onSortChange(value as any);
                  },
                }),
                autotip({
                  isLoading: loading, // 分类未初始化完成时为loading状态
                  loadingText: formatMessage({ id: "dbl_common_loading" }),
                  errorText: formatMessage({ id: "dbl_common_load_fail" }),
                  isError: !!error,
                  isFound: Boolean(searchWords) && total === 0, // 是否显示
                  foundText: formatMessage({ id: "dbl_common_not_search" }),
                  clearResultText: formatMessage({ id: "dbl_common_go_back_old" }),
                  onClear: () => {
                    // 搜索结果为空时, 显示返回原列表按钮
                    setSearchWords('');
                    setSearchValue('');
                    handleClearSearch();
                  },
                  style: { lineHeight: '200px' },
                }),
              ]}
            />
          </Card.Body>
        </Card>
        { /** 详情弹窗 */}
        <DetailModal
          visible={detailVisible}
          name={detailData?.name}
          yaml={detailData?.yaml_context}
          isLoading={detailLoading}
          isError={detailError}
          onClose={() => setDetailVisible(false)}
          formatMessage={formatMessage}
        />
        { /** 删除弹窗 */}
        <ModelModal
          visible={deleteVisible}
          title={formatMessage({ id: 'dbl_model_management_sure_to_delete_model_title' })}
          desc={formatMessage({ id: "dbl_model_management_sure_to_delete_model_message" }, { name: <span className="dbl_text-strong">{deleteInfo?.name}</span> })}
          confirmText={formatMessage({ id: 'dbl_common_confirm' })}
          cancelText={formatMessage({ id: 'dbl_common_cancel' })}
          onClose={() => setDeleteVisible(false)}
          confirmLoading={deleteLoading}
          onConfirm={() => {
            deleteDetailYaml(deleteInfo?.uuid as string);
          }}
          onCancle={() => {
            setDeleteVisible(false);
          }}
        />
        { /** 权限弹窗 */}
        <AccessSettingModal
          visible={accessInfo.id !== -1}
          onClose={isSuccess => {
            setAccessInfo({ id: -1, editors: [], creatorId: '' });
            if (isSuccess) {
              void getTableData(pageInfo, sort, quickValue).catch(() => {
                notification.warning({
                  className: YAML_TEST_RESULT_NOTIFICATION_CLASS,
                  description: formatMessage({
                    id: 'dbl_model_management_list_refresh_failed',
                  }),
                });
              });
            }
          }}
          id={accessInfo?.id as number}
          creatorId={accessInfo?.creatorId}
          editors={accessInfo?.editors}
          type='cube'
        />
      </Content.Body>
    </Content>
  );
};


export default connect((state: any) => ({
  allPrivileges: state.privilegesConfig.allPrivileges,
  gameCode: state.datalab.gameCode,
  userId: state?.home?.userInfo?.userid,
}))(ModelManagementCenter);